// src/app/app.routes.ts
import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import {
  arriendoGuard, moduloGastosGuard, rentabilidadGuard, dashboardGuard, deteriorosGuard, horometroGuard, servicioTecnicoGuard,
} from './core/guards/instance-mode.guard';
import { LayoutComponent } from './layout/layout.component';
import { SrErrorPageComponent } from './shared/components/sr-error-page/sr-error-page.component';

export const routes: Routes = [
  // ===== Páginas sistémicas (F7) — full-screen, fuera del shell de gestión =====
  { path: 'error', component: SrErrorPageComponent, data: { variant: '500' } },
  { path: 'mantenimiento', component: SrErrorPageComponent, data: { variant: 'maint' } },
  // El comodín *.softrent.cl hace que cualquier subdominio cargue la app: estas
  // dos rutas son a dónde va el usuario cuando su dominio no resuelve a ningún
  // cliente activo.
  { path: 'instancia-no-encontrada', component: SrErrorPageComponent, data: { variant: 'sin-instancia' } },
  { path: 'instancia-suspendida', component: SrErrorPageComponent, data: { variant: 'suspendida' } },
  {
    // Sin guard acá: cada pantalla de adentro pone el suyo. Restablecer la
    // contraseña se abre desde un correo y tiene que funcionar aunque haya una
    // sesión abierta en el navegador; con el guard en el padre rebotaba al inicio.
    path: 'auth',
    loadChildren: () =>
      import('./features/auth/auth.routes').then(m => m.AUTH_ROUTES),
  },
  // La dirección corta, la que publica la landing (`<empresa>.softrent.cl/login`).
  // Sin esta ruta la tomaba el shell como un enlace profundo: la guardaba en
  // `returnUrl` y, después de entrar, devolvía a la persona a la página 404.
  { path: 'login', pathMatch: 'full', redirectTo: 'auth/login' },
  // Catálogo público del cliente. Sin guard a propósito: quien entra no es
  // usuario del sistema, viene desde un enlace que le mandó su proveedor. El
  // backend decide si la cuenta publica o no.
  {
    path: 'catalogo',
    loadComponent: () =>
      import('./features/catalogo-publico/catalogo-publico.component')
        .then(m => m.CatalogoPublicoComponent),
    title: 'Catálogo de equipos',
  },

  // Responder la cotización desde el enlace del correo (E3.2-ui · D8).
  //
  // Va acá y NO bajo `/portal` a propósito: ahí la tomaría `portalGuard`, que
  // exige sesión, y quien abre este enlace justamente no la tiene. El token de
  // la URL es toda la credencial.
  {
    path: 'public/aprobar-cotizacion',
    loadComponent: () =>
      import('./features/portal/publico/aprobar-cotizacion-publica.component')
        .then(m => m.AprobarCotizacionPublicaComponent),
    title: 'Aprobar cotización',
  },

  // Firmar una cotización comercial desde el correo: gemela de la anterior para el arriendo.
  {
    path: 'public/firmar-cotizacion',
    loadComponent: () =>
      import('./features/cotizaciones/publico/firma-cotizacion-publica.component')
        .then(m => m.FirmaCotizacionPublicaComponent),
    title: 'Firmar cotización',
  },

  // Verificación pública de documentos (E11 #862): a donde lleva el QR del
  // certificado. Sin guard y fuera del shell por lo mismo que la
  // aprobación pública: la abre cualquiera, desde un teléfono, sin sesión.
  // `/v` sola es el formulario para tipear el código impreso.
  {
    path: 'v',
    loadComponent: () =>
      import('./features/verificacion-publica/verificacion-publica.component')
        .then(m => m.VerificacionPublicaComponent),
    title: 'Verificar documento',
  },
  {
    path: 'v/:codigo',
    loadComponent: () =>
      import('./features/verificacion-publica/verificacion-publica.component')
        .then(m => m.VerificacionPublicaComponent),
    title: 'Verificar documento',
  },

  // Portal del cliente (E1.3). Cuelga de la raíz y no del shell de gestión
  // porque no comparte nada con él: otro encabezado, otra navegación y otra
  // audiencia — gente de fuera de la empresa. Sin guard acá; cada ruta de
  // adentro pone el suyo, porque el login y el alta de contraseña se visitan
  // justamente sin sesión.
  {
    path: 'portal',
    loadChildren: () =>
      import('./features/portal/portal.routes').then(m => m.PORTAL_ROUTES),
  },

  // ===== Design System v16 — showcase/playground (sin guard, preview QA) =====
  {
    path: 'design-system',
    loadComponent: () =>
      import('./features/design-system/showcase.component').then(m => m.ShowcaseComponent),
  },
  {
    // Vista operador (PWA) — layout minimal propio, fuera del shell de gestión.
    path: 'operador',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./features/operador/operador.routes').then(m => m.OPERADOR_ROUTES),
    data: { roles: ['ROLE_OPERATOR', 'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
  },
  {
    path: '',
    component: LayoutComponent,
    canMatch: [AuthGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

      // ========== DASHBOARD ==========
      {
        path: 'dashboard',
        // En COSTOS redirige a /rentabilidad (el home de Michael); en COMPLETO se monta normal.
        canMatch: [dashboardGuard],
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },

      // ========== MAESTROS (Datos base) ==========
      {
        path: 'clientes',
        loadChildren: () => import('./features/clientes/clientes.routes').then(m => m.CLIENTES_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },
      {
        path: 'proveedores',
        loadChildren: () => import('./features/proveedores/proveedores.routes').then(m => m.PROVEEDORES_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },
      {
        path: 'ordenes-compra',
        loadChildren: () => import('./features/ordenes-compra/ordenes-compra.routes').then(m => m.ORDENES_COMPRA_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },
      {
        path: 'equipos',
        loadChildren: () => import('./features/equipos/equipos.routes').then(m => m.EQUIPOS_ROUTES),
        // La flota propia es del arriendo: una cuenta solo de taller atiende máquinas de sus clientes.
        canMatch: [arriendoGuard],
        canActivate: [AuthGuard],
        // ROLE_TECNICO entra a ver la máquina (#853) — el formulario de alta
        // y los botones de editar ya se ocultan en el componente para quien
        // no es GESTION, así que esto solo abre la lectura.
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR', 'ROLE_TECNICO',
          'ROLE_SUPERVISOR_SERVICIO'] }
      },
      {
        path: 'productos',
        loadChildren: () => import('./features/productos/productos.routes').then(m => m.PRODUCTOS_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },
      {
        // El catálogo de servicios que se elige al cotizar. Solo gestión (el backend exige FINANZAS) y solo si
        // el tenant tiene el servicio técnico, que es de donde nace el catálogo.
        path: 'servicios',
        loadChildren: () => import('./features/servicios/servicios.routes').then(m => m.SERVICIOS_ROUTES),
        canMatch: [servicioTecnicoGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },
      {
        path: 'obras',
        loadChildren: () => import('./features/obras/obras.routes').then(m => m.OBRAS_ROUTES),
        canMatch: [arriendoGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },
      {
        path: 'trabajadores',
        loadChildren: () => import('./features/trabajadores/trabajadores.routes').then(m => m.TRABAJADORES_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },
      {
        path: 'flota',
        loadChildren: () => import('./features/flota/flota.routes').then(m => m.FLOTA_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },
      {
        path: 'deterioros',
        loadChildren: () => import('./features/deterioros/deterioros.routes').then(m => m.DETERIOROS_ROUTES),
        canMatch: [arriendoGuard, deteriorosGuard],
      },
      {
        path: 'servicio-tecnico',
        loadChildren: () => import('./features/servicio-tecnico/servicio-tecnico.routes').then(m => m.SERVICIO_TECNICO_ROUTES),
        canMatch: [servicioTecnicoGuard],
        canActivate: [AuthGuard],
        // El mecánico ES el usuario del taller: recibe la máquina, saca las
        // fotos, anota el trabajo y avisa cuando termina. Antes entraba con
        // OPERATOR (un rol para tres pegas); ahora tiene el suyo, ROLE_TECNICO
        // (#853) — se mantiene OPERATOR también porque algunas instancias
        // todavía no migraron cuentas existentes. Lo que no puede hacer —poner
        // precios, aprobar la cotización, entregar, certificar— lo corta
        // `@PreAuthorize(GESTION)` endpoint por endpoint, que es donde la
        // regla se puede sostener.
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR', 'ROLE_TECNICO',
          'ROLE_SUPERVISOR_SERVICIO'] }
      },
      {
        // Hermana de servicio-tecnico y no hija: ver la explicación en
        // `solicitudes.routes.ts`. Mismo gate de feature y mismos roles — la
        // atiende la misma gente que recibe la máquina.
        path: 'solicitudes-servicio',
        loadChildren: () => import('./features/servicio-tecnico/solicitudes/solicitudes.routes')
          .then(m => m.SOLICITUDES_SERVICIO_ROUTES),
        canMatch: [servicioTecnicoGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },
      {
        // Los leads de la landing: otra pantalla y no una fila más de `solicitudes-servicio`,
        // porque quien escribe aún no es cliente y hay datos personales (solo GESTION).
        path: 'solicitudes-web',
        loadChildren: () => import('./features/servicio-tecnico/solicitudes-web/solicitudes-web.routes')
          .then(m => m.SOLICITUDES_WEB_ROUTES),
        canMatch: [servicioTecnicoGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },
      {
        path: 'jornadas',
        loadChildren: () => import('./features/jornadas/jornadas.routes').then(m => m.JORNADAS_ROUTES),
        canMatch: [arriendoGuard, horometroGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },

      // ========== ARRIENDO (Flujo operativo) ==========
      {
        path: 'cotizaciones',
        loadChildren: () => import('./features/cotizaciones/cotizaciones.routes').then(m => m.COTIZACIONES_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },
      {
        path: 'contratos',
        loadChildren: () => import('./features/contratos/contratos.routes').then(m => m.CONTRATOS_ROUTES),
        canMatch: [arriendoGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },

      // ========== GASTOS POR MÁQUINA (modo COSTOS, epic Haiken #555) ==========
      {
        path: 'gastos',
        loadChildren: () => import('./features/gastos/gastos.routes').then(m => m.GASTOS_ROUTES),
        canMatch: [moduloGastosGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },

      // ========== RENTABILIDAD (modo COSTOS, epic Haiken #559 — R1) ==========
      {
        path: 'rentabilidad',
        loadChildren: () => import('./features/rentabilidad/rentabilidad.routes').then(m => m.RENTABILIDAD_ROUTES),
        canMatch: [rentabilidadGuard],
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },

      // ========== FACTURACIÓN (Unificado: Guías, Estados de Pago, Documentos) ==========
      {
        path: 'facturacion',
        loadChildren: () => import('./features/facturacion/facturacion.routes').then(m => m.FACTURACION_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'] }
      },

      // ========== CONFIGURACIÓN ==========
      {
        path: 'configuracion',
        loadChildren: () => import('./features/configuracion/configuracion.routes').then(m => m.CONFIGURACION_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] }
      },
      // La auditoría no es una vista (Samuel, 23-oct-2026): es interna, para que SoftRent mapee quién cambió qué. Se sigue
      // registrando en el backend; en la aplicación no aparece para nadie, ni por un enlace guardado.
      {
        path: 'perfil',
        loadChildren: () => import('./features/perfil/perfil.routes').then(m => m.PERFIL_ROUTES),
      },

      // ========== ADMINISTRACIÓN (Solo Super Admin) ==========
      {
        path: 'admin',
        loadChildren: () => import('./features/admin/admin.routes').then(m => m.ADMIN_ROUTES),
        canActivate: [AuthGuard],
        data: { roles: ['ROLE_SUPER_ADMIN'] }
      }
    ]
  },
  // Ruta no encontrada → página 404 de marca (antes redirigía silenciosamente a '') (F7)
  { path: '**', component: SrErrorPageComponent, data: { variant: '404' } }
];
